import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { faBan, faChevronRight, faCrown, faPlusCircle } from "@fortawesome/pro-regular-svg-icons";
import { faTag } from "@fortawesome/pro-solid-svg-icons";
import { IAPIResponse, StatusCode } from "api/protocols";
import { INotification } from "api/rpc/2024-04/customer/teesheet";
import classNames from "classnames";
import Callout from "components/callout/Callout";
import LoaderCard from "components/loaderCard/LoaderCard";
import { convertTime, convertHoles } from "helpers/Helpers";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { groupBy } from "lodash";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { IBookingParticipant, ISlot, ITeeTime } from "redux/reducers/models/teetime";
import "./clientPortalTeeSheet.scss";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import useModal from "hooks/modals/useModal";
import { ILottery } from "redux/reducers/models/lottery";
import CustomerLotteryRequestModal from "../CustomerLotteryRequestModal/CustomerLotteryRequestModal";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { CustomerGetLottery, CustomerPostLotteryRequest, IPostLotteryRequestParams } from "api/rpc/2024-04/customer/lottery";
import { GetTeeTimesCustomer } from "api/rpc/2022-09/customer/teetime";
import Input from "components/form/input";
import { ButtonNew as Button } from "components/buttonNew";
import Tabs from "components/tabs/Tabs";
import { IFilterState } from "../ClientPortalHome";

interface IProps {
  checkTeeTime: (date: string, teeTimeId: ITeeTime, slot?: ISlot) => Promise<void>;
  teeTimeRes: IAPIResponse<Array<ITeeTime>>;
  notifications: Array<INotification>;
  showRestrictedBookings: boolean;
  getTeeTimesAccessCode: (accessCode: string, teesheet: boolean) => Promise<void>;
  leagueSelected?: boolean;
  filterState: Partial<IFilterState>;
  onCourseSelect: (courseId: number) => void;
}

type TSlotGroup = Array<Array<ISlot>>;

interface IState {
  teeTimes: Array<ITeeTime & { slotGroups: TSlotGroup }>;
}

export default function ClientPortalTeeSheet(props: IProps) {
  const { checkTeeTime, teeTimeRes, notifications, showRestrictedBookings, getTeeTimesAccessCode, leagueSelected, filterState, onCourseSelect } = props;
  const {t} = useTranslation();
  const windowSize = useWindowSize();
  const dispatch = useAppDispatch();
  const { clientPortalStore } = useAppSelector(store => store);

  const [state, setState] = useState<IState>({
    teeTimes: null,
  });

  const [accessCode, setAccessCode] = useState<string>("");

  const {
    state: lotteryRequestModal,
    closeModal: closeLotteryRequestModal,
    updateModal: updateLotteryRequestModal,
  } = useModal<{lottery: ILottery; lotteryTimes: Array<ITeeTime & {slotGroups: TSlotGroup}>; teetime: ITeeTime}>({ lottery: null, lotteryTimes: [], teetime: null});
  
  useEffect(() => {
    if (teeTimeRes) {
      const teeTimes = teeTimeRes?.data;

      let updatedTeeTimes = teeTimeRes?.data?.map((teetime, rowIndex) => {
        return {
          ...teetime,
          slotGroups: Object.values(
            groupBy(teeTimes[rowIndex].slots, s => (s.tee_time_booking_id === null ? s.id : s.tee_time_booking_id)),
          ),
        };
      });

      if (!showRestrictedBookings) {
        updatedTeeTimes = updatedTeeTimes?.filter(teetime => !teetime?.is_restricted);
      }

      setState(prevState => ({ ...prevState, teeTimes: updatedTeeTimes }));
    } else {
      setState(prevState => ({ ...prevState, teeTimes: null }));
    }
  }, [teeTimeRes, showRestrictedBookings]);

  async function getLottery(teetime: ITeeTime & {
    slotGroups: TSlotGroup;
  }) {
    //Cannot request time in the past
    if (moment(teetime?.date)?.isSameOrBefore(moment()) && moment().isAfter(moment(teetime?.start_time, "HH:mm"))) {
      dispatch(showError(t("Cannot request a tee time in the past")));
      return;
    }

    dispatch(enqueue());

    const teetimeRes = await GetTeeTimesCustomer({
      date: teetime?.date,
      id: teetime?.id,
      facility_id: clientPortalStore?.selectedFacility?.id,
      turn_tee_time: true,
      extended: true,
    }, false);

    if (teetimeRes?.status !== StatusCode.OK) {
      dispatch(dequeue());
      dispatch(showError(t("Error getting tee time")));
      return;
    }

    const res = await CustomerGetLottery({id: teetime?.lottery_id, facility_id: clientPortalStore?.selectedFacility?.id}, false);

    if (res?.status !== StatusCode.OK) {
      dispatch(dequeue());
      dispatch(showError(t("Error getting lottery details")));
      return;
    }

    dispatch(dequeue());

    const lotteryTeeTimes = state?.teeTimes?.filter(time => time?.lottery_id === teetime?.lottery_id);

    updateLotteryRequestModal({isOpen: true, lottery: res?.data, lotteryTimes: lotteryTeeTimes, teetime: teetimeRes?.data[0]});
  }

  async function createLotteryRequest(params: IPostLotteryRequestParams) {
    const res = await CustomerPostLotteryRequest(params, true);

    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t(typeof res?.data === "string" ? res?.data : "Error creating lottery request")));
      return;
    }

    dispatch(showSuccess(t("Successfully created tee time lottery request")));
    void closeLotteryRequestModal();
  }

  function handleEnterKeydown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.code === "Enter" || e.code === "NumpadEnter") {
      void getTeeTimesAccessCode(accessCode, true);
    }
  }

  function renderPromotionIcon(teeTime: ITeeTime) {
    if (!teeTime?.promotion_type || teeTime?.promotion_value == null) {
      return null;
    }

    const discount =
      teeTime.promotion_type === "percent"
        ? `${teeTime.promotion_value}% off`
        : `$${teeTime.promotion_value} off`;

    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`promotion-${teeTime.id}`}>
            <div>{teeTime.promotion_label || t("Promotion")}</div>
            <div>{t("{{discount}} online pricing", { discount })}</div>
          </Tooltip>
        }
      >
        <FontAwesomeIcon className="client-portal-slots-promotion-icon" icon={faTag} />
      </OverlayTrigger>
    );
  }

  const coursesTabs = [{content: t("All"), id: -1}]?.concat(clientPortalStore?.selectedFacility?.courses?.map(course => ({content: course?.long_name, id: course?.id})));

  const teeTimes = useMemo(() => {
    if (state?.teeTimes === null) {
      return [...Array(12)]?.map((num, index) => (
        <div key={index} className="client-portal-slots-container">
          <p className="client-portal-slots-start-time"></p>
          {[...Array(4)]?.map((card, cardIndex) => (
            <LoaderCard key={cardIndex} customCSSProperties={{ width: "100%", height: "40px" }} />
          ))}
        </div>
      ));
    } else if ((state?.teeTimes && state?.teeTimes?.length === 0) || state?.teeTimes === undefined) {
      return (<>
        <Callout title={t("No Tee Times")} content={t("There are no tee times for this day")} type="error" />
        {clientPortalStore?.teeSheetSettings?.tee_times_access_code && clientPortalStore?.user && !leagueSelected ? (
          <><br/><Input 
            label={t("Access Code")} 
            tooltipText={t("Enter an access code to access exclusive tee times")} 
            placeholder={t("Access Code")} 
            value={accessCode} 
            onKeyDown={handleEnterKeydown}
            onChange={e => setAccessCode(e.target.value)}
            autoFocus
            trailingButtons={[<Button key={1} type="secondary" onClick={() => getTeeTimesAccessCode(accessCode, true)}>{t("Enter")}</Button>]}
          /></>
        ) : null}
      </>);
    } else {
      return state?.teeTimes?.map((teeTime, index, teeTimes) => {
        const prevTime = teeTimes[index - 1] ? moment(teeTimes[index - 1]?.start_time, "HH:mm").format("hh") : null;
        const currentTime = moment(teeTime?.start_time, "HH:mm").format("hh");
        const prevTeeTime = teeTimes[index - 1];
        const nextTeeTime = teeTimes[index + 1];
        const eventTeeTime = teeTime?.blocked_type === "shotgun" || teeTime?.blocked_type === "shotgun_crossover";
        const lotteryTeeTime = teeTime?.lottery_id != null && teeTime?.blocked_type === "lottery";

        return (
          <React.Fragment key={index}>
            {windowSize.width > MOBILE_WIDTH && (index === 0 || prevTime !== currentTime) ? (
              <div className={classNames("client-portal-slots-hour-block", {
                "client-portal-slots-hour-block-event": lotteryTeeTime && prevTeeTime?.lottery_id === teeTime?.lottery_id,
                "client-portal-slots-container-event": lotteryTeeTime && prevTeeTime?.lottery_id === teeTime?.lottery_id,
              })}>
                <p className="client-portal-slots-hour">
                  {moment(teeTime?.start_time?.split(":")[0] + ":00", "HH:mm").format("h:mm A")}
                </p>
                <div className="client-portal-slots-hour-line"></div>
              </div>
            ) : null}

            {eventTeeTime &&
              prevTeeTime?.blocked_type !== "shotgun" &&
              prevTeeTime?.blocked_type !== "shotgun_crossover" && (
              <div className="client-portal-slots-container client-portal-slots-container-event-header">
                {t("Shotgun")} - {convertTime(teeTime?.start_time)}
              </div>
            )}

            {lotteryTeeTime && !eventTeeTime && prevTeeTime?.lottery_id !== teeTime?.lottery_id && (
              <div className="client-portal-slots-container client-portal-slots-container-event-header">
                <FontAwesomeIcon icon={faCrown} /> {t("Lottery")}
              </div>
            )}

            <div
              className={classNames("client-portal-slots-container", {
                "client-portal-slots-container-event": eventTeeTime || lotteryTeeTime,
                "client-portal-slots-container-event-last":
                  eventTeeTime && (nextTeeTime === undefined || nextTeeTime?.start_time !== teeTime?.start_time) ||
                  lotteryTeeTime && (nextTeeTime === undefined || nextTeeTime?.lottery_id !== teeTime?.lottery_id)
                ,
              })}
            >
              <p className="client-portal-slots-start-time">
                {renderPromotionIcon(teeTime)}
                <span>
                  {teeTime?.blocked_type === "shotgun" || teeTime?.blocked_type === "shotgun_crossover"
                    ? teeTime?.start_hole
                    : convertTime(teeTime.start_time)}
                </span>
              </p>

              <div className="client-portal-slots-group-full-width">
                <div className="client-portal-slots-group-parent-container">
                  {teeTime?.slotGroups?.map((slotGroup, slotGroupIndex) => {
                    return (
                      <div
                        key={slotGroupIndex}
                        className={classNames(
                          "client-portal-slots-group-container w-full",
                          `client-portal-tee-time-slot_spaced_${slotGroup?.length}`,
                          { "client-portal-tee-time-slot_last": teeTime?.slotGroups?.length === slotGroupIndex + 1 },
                        )}
                      >
                        {slotGroup?.map(
                          (
                            slot: ISlot & {
                              booking_participant: IBookingParticipant & { default_booking_category_title: string };
                            },
                            slotIndex,
                          ) => {
                            const participantName = slot?.booking_participant?.guest
                              ? "Guest"
                              : slot.booking_participant?.full_name && slot.booking_participant?.full_name?.trim()
                              ? slot?.booking_participant?.full_name
                              : slot?.booking_participant?.default_booking_category_title
                              ? slot?.booking_participant?.default_booking_category_title
                              : "Golfer";

                            if (slot?.tee_time_booking_participant_id) {
                              if (windowSize.width <= MOBILE_WIDTH) {
                                return (
                                  <div
                                    key={slotIndex}
                                    className={classNames(
                                      "client-portal-slots-single-slot client-portal-slots-participant",
                                      {
                                        "client-portal-slots-disabled": !!slot?.tee_time_booking_participant_id,
                                      },
                                    )}
                                  >
                                    <p className="client-portal-slots-single-slot-name">{participantName}</p>
                                    {participantName !== slot?.booking_participant?.default_booking_category_title && (
                                      <p className="client-portal-slots-single-slot-booking-category">
                                        {slot?.booking_participant?.default_booking_category_title}
                                      </p>
                                    )}
                                    <p className="client-portal-slots-single-slot-holes">{convertHoles(slot.booking?.holes, clientPortalStore.selectedFacility?.courses[0])}</p>
                                  </div>
                                );
                              } else {
                                return (
                                  <div
                                    key={slotIndex}
                                    className={classNames(
                                      "client-portal-slots-single-slot client-portal-slots-participant",
                                      {
                                        "client-portal-slots-disabled": !!slot?.tee_time_booking_participant_id,
                                      },
                                    )}
                                  >
                                    <p className="client-portal-slots-single-slot-holes">{convertHoles(slot.booking?.holes, clientPortalStore.selectedFacility?.courses[0])}</p>
                                    <div style={{ display: "flex", flexDirection: "column", minWidth: "0" }}>
                                      <p className="client-portal-slots-single-slot-name">{participantName}</p>
                                      {participantName !== slot?.booking_participant?.default_booking_category_title && (
                                        <p className="client-portal-slots-single-slot-booking-category">
                                          {slot?.booking_participant?.default_booking_category_title}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                );
                              }
                            } else {
                              //Block tee time if only 1 slot is available and single bookings are not allowed
                              const blockTeeTime =
                                teeTime?.quantity_remaining === 1 &&
                                clientPortalStore?.teeSheetSettings?.single_bookings === "none";

                              const isLottery = teeTime?.blocked_type === "lottery" && teeTime?.lottery_id != null;

                              return (
                                <div
                                  key={slotIndex}
                                  className={classNames("client-portal-slots-single-slot", {
                                    "client-portal-slots-disabled": blockTeeTime,
                                    "client-portal-slots-restricted": teeTime?.is_restricted,
                                  })}
                                  onClick={() => isLottery ? getLottery(teeTime) :
                                    !blockTeeTime ? checkTeeTime(teeTime.date, teeTime, slot) : undefined
                                  }
                                >
                                  {teeTime?.is_restricted ? (
                                    <OverlayTrigger
                                      placement="auto"
                                      overlay={<Tooltip id="Restricted-booking"><>{t("Restricted tee time")} <br/> {t("Your member rate is not valid at this time. Regular green fee applies.")}</></Tooltip>}
                                    ><span><p className="client-portal-slots-restricted-text">{t('Restricted booking')}<FontAwesomeIcon icon={faChevronRight} /></p></span></OverlayTrigger>
                                  ) : (
                                    <FontAwesomeIcon
                                      icon={blockTeeTime ? faBan : faPlusCircle}
                                      size="1x"
                                      className="client-portal-slots-single-slot-add"
                                    />
                                  )}
                                  
                                </div>
                              );
                            }
                          },
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      });
    }
  }, [state.teeTimes, accessCode]);

  return (
    <div className="client-portal-tee-sheet">
      <div>
        {notifications?.map((notification, index) => (
          <Callout key={index} type="info" title={notification.subject} markdown={notification.content} />
        ))}
      </div>

      {clientPortalStore?.selectedFacility?.courses?.length > 1 ? (
        <div className="flex justify-end">
          <Tabs 
            variant="underline" 
            tabs={coursesTabs} 
            selected={coursesTabs?.findIndex(course => course?.id === filterState?.selectedCourseId) ?? 0} 
            onSelect={tabIndex => onCourseSelect(coursesTabs[tabIndex]?.id)}/>
          <br />
        </div>
      ) : null}
      
      {teeTimes}

      {/* Lottery Request Modal */}
      <CustomerLotteryRequestModal 
        open={lotteryRequestModal?.isOpen}
        onCancel={closeLotteryRequestModal}
        onOk={createLotteryRequest}
        facility={clientPortalStore?.selectedFacility}
        lottery={lotteryRequestModal?.lottery}
        lotteryTimes={lotteryRequestModal?.lotteryTimes}
        teetime={lotteryRequestModal?.teetime}
      />
    </div>
  );
}